/** Snap minutes to nearest quarter (0, 15, 30, 45) */
export function snapToQuarter(min: number): number {
  const snapped = Math.round(min / 15) * 15;
  return snapped >= 60 ? 45 : snapped < 0 ? 0 : snapped;
}

/** Get next quarter-hour from now (at least 1 minute in future) */
export function getNextQuarterHour(): { hour24: number; minute: number } {
  const now = new Date();
  const totalMin = now.getHours() * 60 + now.getMinutes();
  const next = Math.ceil((totalMin + 1) / 15) * 15;
  const capped = Math.min(next, 23 * 60 + 45); // Cap at 23:45 today
  const hour24 = Math.floor(capped / 60);
  const minute = capped % 60;
  return { hour24, minute };
}

/** Parse "HH:MM" (24h) to { hour12, minute, isPM } */
export function parseHHMM(
  str: string
): { hour12: number; minute: number; isPM: boolean } | null {
  const trimmed = (str ?? '').toString().trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const h24 = Number(match[1]);
  const min = Number(match[2]);
  if (
    !Number.isFinite(h24) ||
    !Number.isFinite(min) ||
    h24 < 0 ||
    h24 > 23 ||
    min < 0 ||
    min > 59
  ) {
    return null;
  }
  let hour12: number;
  let isPM: boolean;
  if (h24 === 0) {
    hour12 = 12;
    isPM = false;
  } else if (h24 >= 1 && h24 <= 11) {
    hour12 = h24;
    isPM = false;
  } else if (h24 === 12) {
    hour12 = 12;
    isPM = true;
  } else {
    hour12 = h24 - 12;
    isPM = true;
  }
  return { hour12, minute: snapToQuarter(min), isPM };
}

/** Convert hour12, minute, isPM to "HH:MM" (24h) */
export function toHHMM(
  hour12: number,
  minute: number,
  isPM: boolean
): string {
  let h24: number;
  if (isPM) {
    h24 = hour12 === 12 ? 12 : hour12 + 12;
  } else {
    h24 = hour12 === 12 ? 0 : hour12;
  }
  const h = String(h24).padStart(2, '0');
  const m = String(minute).padStart(2, '0');
  return `${h}:${m}`;
}
